import { Measurement, initializeRumClient, rumClient } from '@m/rum-client';
import { Event, domain } from '@m/rum-client/events';
import { signalCategories } from '@m/rum-client/signal-categories';
import { cloneDeepWith, isPlainObject, noop, pickBy, toString } from 'lodash';
import { SurveyData } from 'types/SurveyData';

import { getApplicationVersion } from '@utils/meta';

import JQueryDetector from './detector-jquery';
import { isTelemetryServiceEnabled } from './utils';

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

type SessionAttributesConfig = Pick<
  SurveyData,
  | 'pagerPage'
  | 'pagerPercent'
  | 'clientId'
  | 'surveyid'
  | 'title'
  | 'specId'
  | 'desktop_params'
  | 'environment'
  | 'datacenter'
  | 'express_cluster'
  | 'express_cluster_url'
  | 'express_company_name'
  | 'survey_engine_cluster_url'
>;

interface ComponentStartMark {
  questionId: string;
  questionType: string;
  questionCaption: string;
}

interface ComponentEndMark extends Pick<ComponentStartMark, 'questionId'> {
  mountDuration: number;
}

// ----------------------------------------------------------------------
// CONFIG
// ----------------------------------------------------------------------

const UKN_PARAM = 'unknown';
const APP_NAME = '@m/react-surveys';

function getClientMeta(sessionAttributes: Record<string, string>) {
  const VERSION = getApplicationVersion();

  return {
    application: {
      name: APP_NAME,
      version: VERSION,
      attributes: {}
    },
    service: {
      name: APP_NAME,
      version: VERSION
    },
    user: {
      id: UKN_PARAM,
      attributes: {}
    },
    session: {
      attributes: sessionAttributes
    }
  };
}

// ----------------------------------------------------------------------
// UTILS
// ----------------------------------------------------------------------

export function getResourceDuration(resourceStats: PerformanceResourceTiming) {
  return Math.round(resourceStats.duration || resourceStats.responseEnd - resourceStats.fetchStart);
}

export function isResourceCached(resourceStats: PerformanceResourceTiming) {
  return resourceStats.transferSize === 0;
}

export function normalizeEventPayload<Attributes>(
  record: Record<string, any>
): Record<keyof Attributes, string> {
  return cloneDeepWith(record, (value) => {
    return !isPlainObject(value) ? toString(value) : undefined;
  });
}

export function normalizeFeatureFlagsPayload(payload: SurveyData['featureFlags']) {
  return Object.keys(payload)
    .map((key) => ({ key, value: payload[key as keyof typeof payload] }))
    .filter((featureFlag) => Boolean(featureFlag.value))
    .map((featureFlag) => featureFlag.key)
    .join(', ');
}

// ----------------------------------------------------------------------
// EVENTS
// ----------------------------------------------------------------------

export class CacheHitEvent extends Event {
  constructor() {
    super({
      name: 'event_browser_cache_hit',
      domain: domain.SESSION,
      attributes: { label: 'Application bundle loaded from cache' }
    });
  }
}

type LivingLensEventType = 'UPLOADED' | 'RECORDED' | 'RESET';
interface LivingLensEventAttributes {
  questionId: number;
  questionCaption: string;
  eventType: LivingLensEventType;
  uploadValidationRetries?: number;
}

export class LivingLensEvent extends Event {
  constructor(attributes: Record<keyof LivingLensEventAttributes, string>) {
    super({
      name: 'event_block_livinglens',
      domain: domain.SESSION,
      attributes: { ...attributes, label: 'Living Lens Event' }
    });
  }
}

export class DebuggerOpenedEvent extends Event {
  constructor() {
    super({
      name: 'event_debugger_opened',
      domain: domain.SESSION,
      attributes: { label: 'Runtime Debugger Opened' }
    });
  }
}

type DebuggerActionType = 'download-survey-data' | 'download-snapshot' | 'download-spec';

interface DebuggerActionEventAttributes {
  actionType: DebuggerActionType;
  surveyId: string;
}

export class DebuggerActionEvent extends Event {
  constructor(attributes: Record<keyof DebuggerActionEventAttributes, string>) {
    super({
      name: 'event_debugger_action',
      domain: domain.SESSION,
      attributes: { ...attributes, label: 'Debugger Action' }
    });
  }
}

export class SurveySubmittedEvent extends Event {
  constructor() {
    super({
      name: 'event_survey_submitted',
      domain: domain.SESSION,
      attributes: { label: 'Survey Submitted' }
    });
  }
}

export type AutoSaveGuardResult = 'guard_blocked' | 'guard_passed';

type AutoSaveGuardBlockedAttributes = {
  result: 'guard_blocked';
  confidence: number;
  detectedSignals: string;
};

type AutoSaveGuardPassedAttributes = {
  result: 'guard_passed';
  isTrusted: boolean;
};

type AutoSaveGuardEventAttributes = AutoSaveGuardBlockedAttributes | AutoSaveGuardPassedAttributes;

export class AutoSaveGuardEvent extends Event {
  constructor(attributes: Record<string, string>) {
    super({
      name: 'event_auto_save_guard',
      domain: domain.SESSION,
      attributes: { ...attributes, label: 'Auto-Save Guard' }
    });
  }
}

// ----------------------------------------------------------------------
// CORE
// ----------------------------------------------------------------------

export class TelemetryService {
  isInitialized = false;
  pendingEvents: Parameters<typeof rumClient.sendEvent>[0][] = [];
  pageLoadMeasurement?: Measurement;
  componentMeasurements: Map<string, Measurement> = new Map();

  private createSessionAttributes(config: SessionAttributesConfig) {
    return {
      host: new URL(window.location.href).hostname,
      progress_page_number: String(config.pagerPage + 1),
      progress_percentage: String(config.pagerPercent),
      ...pickBy({
        survey_title: config.title,
        survey_id: config.surveyid,
        spec_id: config.specId,
        company_name: config.express_company_name,
        survey_engine_client_id: config.clientId,
        // ↓ This is NOT equivalent to the `survey_engine` session attribute
        // sent by Reporting and OA suite.
        // survey_engine_cluster_url = 'http://neechsurveytst.qa.den.medallia.com'
        // survey_engine = 'neech-survey-be.den.medallia.com'
        survey_engine_cluster_url: config.survey_engine_cluster_url,
        // ↓ This is equivalent to `host` sent by Reporting and OA suite.
        express_cluster_url: config.express_cluster_url,
        // ↓ Shared MEC labels
        environment: config.environment,
        datacenter: config.datacenter,
        express_cluster: config.express_cluster
      })
    };
  }

  // ----------------------------------------------------------------------
  // INIT
  // ----------------------------------------------------------------------

  public initializeSession() {
    if (!isTelemetryServiceEnabled()) {
      console.warn('RUM tracking is not enabled. Session initialization skipped.');
      return noop();
    }

    if (!this.isInitialized) {
      const surveyData = window.surveyData;

      window._RUM_MONITORING_DEFAULT_SIGNALS_CONFIG_ = {
        event: true,
        exception: true,
        log: true,
        measurement: true,
        span: true,
        unknown: false
      };

      initializeRumClient({
        collectUrl: surveyData.rumconfigblock.grafana_collect_url,
        apiKey: surveyData.rumconfigblock.grafana_collect_api_key,
        meta: getClientMeta(this.createSessionAttributes(surveyData)),
        enabledSignals: window._RUM_MONITORING_DEFAULT_SIGNALS_CONFIG_
      });

      this.isInitialized = true;
      this.flushPendingEvents();
    }

    this.pageLoadMeasurement = new Measurement({
      key: 'page_load',
      category: signalCategories.PAGE,
      attributes: { label: 'Page Loaded' }
    });
  }

  // ----------------------------------------------------------------------
  // MEASUREMENTS
  // ----------------------------------------------------------------------

  public markDOMInteractive() {
    if (!this.isInitialized || !this.pageLoadMeasurement) {
      return noop();
    }

    const entry = performance.getEntriesByType('navigation')[0];
    const { domInteractive } = entry as PerformanceNavigationTiming;

    this.pageLoadMeasurement.payload.dom_interactive = domInteractive;
  }

  public markBrandingCSSLoadTime() {
    if (!this.isInitialized || !this.pageLoadMeasurement) {
      return noop();
    }

    const resources = performance.getEntriesByType('resource') as Array<PerformanceResourceTiming>;
    const brandingCSSresources = resources.filter(
      (resource) =>
        resource.initiatorType === 'link' &&
        !resource.name.includes('main.css') &&
        !resource.name.includes('.chunk.css')
    );

    const brandingCSSresourcesDuration = brandingCSSresources
      .map(getResourceDuration)
      .reduce((total, next) => total + next, 0);

    this.pageLoadMeasurement.payload.branding_css = brandingCSSresourcesDuration;
  }

  public markJQueryLoadTime() {
    if (!this.isInitialized || !this.pageLoadMeasurement) {
      return noop();
    }

    const resources = performance.getEntriesByType('resource') as Array<PerformanceResourceTiming>;
    const jQueryResource = resources.find((resource) => resource.name.includes('jquery'));

    if (jQueryResource) {
      const jQueryResourceDuration = getResourceDuration(jQueryResource);
      this.pageLoadMeasurement.payload.jquery = jQueryResourceDuration;
    }
  }

  public sendPageLoadMeasurement() {
    if (this.isInitialized && this.pageLoadMeasurement) {
      this.pageLoadMeasurement.end();
      this.pageLoadMeasurement.send();
    }
  }

  public markComponentStart({ questionId, questionType, questionCaption }: ComponentStartMark) {
    if (!this.isInitialized) {
      return noop();
    }

    const measurement = new Measurement({
      key: 'component_load',
      category: signalCategories.COMPONENT,
      attributes: {
        question_id: questionId,
        question_type: questionType,
        question_caption: questionCaption,
        label: `Question Loaded: ${questionType}`
      }
    });

    this.componentMeasurements.set(questionId, measurement);
  }

  public markComponentEnd({ questionId, mountDuration }: ComponentEndMark) {
    if (!this.isInitialized) {
      return noop();
    }

    const measurement = this.componentMeasurements.get(questionId);

    if (measurement) {
      measurement.payload.mount = mountDuration;
      measurement.end();
      measurement.send();
      this.componentMeasurements.delete(questionId);
    }
  }

  // ----------------------------------------------------------------------
  // EVENTS
  // ----------------------------------------------------------------------

  private flushPendingEvents() {
    this.pendingEvents.forEach((event) => rumClient.sendEvent(event));
    this.pendingEvents = [];
  }

  sendEvent = (evt: Event) => {
    const payload = {
      name: evt.name,
      domain: evt.domain,
      category: evt.getCategory(),
      attributes: evt.attributes
    };

    if (!this.isInitialized) {
      this.pendingEvents.push(payload);
      return;
    }

    rumClient.sendEvent(payload);
  };

  public trackJQueryUsage() {
    JQueryDetector.trackUsedAPIs(this.sendEvent);
  }

  public trackBrowserCacheUsage() {
    const trackeableResources = ['main.css', 'main.legacy.js', 'main.modern.js'];
    const resources = performance.getEntriesByType('resource') as Array<PerformanceResourceTiming>;

    const trackedResources = resources.filter((resource) =>
      trackeableResources.some((res) => resource.name.includes(res))
    );

    const isCacheUsed = trackedResources.some((resource) => isResourceCached(resource));
    if (isCacheUsed) {
      const signal = new CacheHitEvent();
      this.sendEvent(signal);
    }
  }

  public trackLivingLensEvent(attributes: LivingLensEventAttributes) {
    const normalizedAttributes = normalizeEventPayload<typeof attributes>(attributes);
    const signal = new LivingLensEvent(normalizedAttributes);
    this.sendEvent(signal);
  }

  public trackDebuggerOpened() {
    const signal = new DebuggerOpenedEvent();
    this.sendEvent(signal);
  }

  public trackDebuggerAction(attributes: DebuggerActionEventAttributes) {
    const normalizedAttributes = normalizeEventPayload<typeof attributes>(attributes);
    const signal = new DebuggerActionEvent(normalizedAttributes);
    this.sendEvent(signal);
  }

  public trackSurveySubmitted() {
    const signal = new SurveySubmittedEvent();
    this.sendEvent(signal);
  }

  public trackAutoSaveGuard(attributes: AutoSaveGuardEventAttributes) {
    const normalizedAttributes = normalizeEventPayload<typeof attributes>(attributes);
    const signal = new AutoSaveGuardEvent(normalizedAttributes);
    this.sendEvent(signal);
  }
}

export default new TelemetryService();
